import { Box } from '@zoopla-eng/zds-components/box';
import { Divider } from '@zoopla-eng/zds-components/divider';
import { AdSideBarLayout } from '@zoopla-eng/zds-components/page';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { AdvertProvider, AD_UNIT_SECTION } from '@zoopla/adverts/dfp';
import { ArticleCollection } from '@zoopla/cms/article-collection';
import { updateFilter } from '@zoopla/redux-reducer-filters';
import { usePageTracking } from '@zoopla/tracking';
import { NewHomesAdverts } from '../components/NewHomesAdverts';
import { NewHomesArticleCollection } from '../components/NewHomesArticleCollection';
import { NewHomesBody } from '../components/NewHomesBody';
import { NewHomesContent } from '../components/NewHomesContent';
import { NewHomesHeroImage } from '../components/NewHomesHeroImage';
import { NewHomesSectionTitle } from '../components/NewHomesSectionTitle';
import { NewHomesSubNav } from '../components/NewHomesSubNav';
import { NewHomesTownsAndCitiesLinks } from '../components/NewHomesTownsAndCitiesLinks';
import { NewHomesFaq, newHomesFaqContent } from '../components/NewHomesFaq';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { SearchInitiationMobile } from '@zoopla/listing-search/search-initiation';
import { NewHomesHead } from '../components/NewHomesHead/NewHomesHead';
import { BoxBleed, ContentWrapper, HeroWrapper } from './NewHomesPage.styles';

type NewHomesPageProps = {
    articleCollection?: ArticleCollection;
};

const NewHomesPage = ({ articleCollection }: NewHomesPageProps) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(updateFilter('newHomes', true));
    }, [dispatch]);

    usePageTracking('/new-homes/', {
        section: 'new-homes'
    });

    return (
        <>
            <NewHomesHead />
            <AdSideBarLayout
                header={<div></div>}
                main={
                    <BoxBleed fw>
                        <Box px="$S400" bpM100={{ pt: '$S500' }} fw>
                            <Stack
                                rowGap="$M300"
                                items={{
                                    width: '100%'
                                }}
                            >
                                <HeroWrapper>
                                    <Box bpM100={{ display: 'none' }}>
                                        <NewHomesHeroImage />
                                    </Box>
                                    <Box bpM100={{ maxWidth: 'min-content' }}>
                                        <ContentWrapper>
                                            <Box
                                                pb="$XS400"
                                                {...{
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <NewHomesSectionTitle>
                                                    New homes for sale
                                                </NewHomesSectionTitle>
                                            </Box>
                                            <NewHomesBody>
                                                Search new-build houses, flats,
                                                and developments across the UK
                                            </NewHomesBody>
                                            <SearchInitiationMobile
                                                updateResultsText="Search"
                                                searchType="new-homes"
                                                eventCategory="new_homes"
                                                searchSource="new-homes"
                                                showRadius={false}
                                                hideTabs
                                                excludePropertyTypes={[
                                                    'farms_land'
                                                ]}
                                            />
                                        </ContentWrapper>
                                    </Box>
                                </HeroWrapper>
                                <NewHomesSubNav />
                                <Divider direction="horizontal" />
                                <NewHomesContent />
                                <Divider direction="horizontal" />
                                {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                                <>
                                    {articleCollection ? (
                                        <NewHomesArticleCollection
                                            articleCollection={
                                                articleCollection
                                            }
                                        />
                                    ) : null}
                                </>
                                <Divider direction="horizontal" />
                                <NewHomesTownsAndCitiesLinks />
                                <Divider direction="horizontal" />
                                <NewHomesFaq
                                    newHomesFaqContent={newHomesFaqContent}
                                />
                            </Stack>
                        </Box>
                    </BoxBleed>
                }
                adverts={
                    <AdvertProvider
                        value={{
                            adBridgPage: AD_UNIT_SECTION.NEW_HOMES,
                            targeting: {
                                page: '/new-homes/'
                            }
                        }}
                    >
                        <NewHomesAdverts />
                    </AdvertProvider>
                }
                bottomRail={<div></div>}
                footer={<div></div>}
            />
        </>
    );
};

export default NewHomesPage;
